import CampaignIcon from "@mui/icons-material/Campaign";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { RaffleRoundStatus, type RaffleRoundDto } from "Infrastructure/Api/Api";
import { Fragment, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Resources, useResource } from "Translations/Resources";
import { formatDate, formatMonthAndYear } from "Utils/DateUtils";

const RESOURCES = Resources.Admin.Raffle;

type Props = {
  rounds: Array<RaffleRoundDto>;
  /** Raffle Update. */
  canManage: boolean;
  /** Raffle Export. */
  canExport: boolean;
  /** Rounds with a lifecycle request in flight; their mutating actions stay disabled. */
  busyRoundIDs: ReadonlyArray<string>;
  /** Rounds being closed; their Close button shows progress instead of the lock. */
  closingRoundIDs: ReadonlyArray<string>;
  isExporting: boolean;
  emptyText: string;
  onView: (round: RaffleRoundDto) => void;
  onEdit: (round: RaffleRoundDto) => void;
  onAnnounce: (round: RaffleRoundDto) => void;
  onClose: (round: RaffleRoundDto) => void;
  onComplete: (round: RaffleRoundDto) => void;
  onCancel: (round: RaffleRoundDto) => void;
  onSetDefault: (round: RaffleRoundDto) => void;
  onExport: (round: RaffleRoundDto) => void;
  onFindHolder: (round: RaffleRoundDto) => void;
};

type MonthGroup = {
  /** "yyyy-MM" of the draw date, or null for rounds not scheduled yet. */
  key: string | null;
  rounds: Array<RaffleRoundDto>;
};

// Planning view (R7): unscheduled first, then draw months newest first; API order inside a group.
const groupByDrawMonth = (rounds: Array<RaffleRoundDto>): Array<MonthGroup> => {
  const byKey = new Map<string | null, Array<RaffleRoundDto>>();
  for (const round of rounds) {
    const key = round.drawDate ? round.drawDate.slice(0, 7) : null;
    byKey.set(key, [...(byKey.get(key) ?? []), round]);
  }
  return [...byKey.entries()]
    .map(([key, groupRounds]) => ({ key, rounds: groupRounds }))
    .toSorted((a, b) => {
      if (a.key === null) {
        return -1;
      }
      if (b.key === null) {
        return 1;
      }
      return b.key.localeCompare(a.key);
    });
};

type RowActionProps = {
  title: string;
  icon: ReactNode;
  isDisabled?: boolean;
  onClick: () => void;
};

// The span keeps the tooltip on a disabled button; aria-label keeps its accessible name.
const RowAction = ({
  title,
  icon,
  isDisabled = false,
  onClick,
}: RowActionProps) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        aria-label={title}
        disabled={isDisabled}
        onClick={onClick}
      >
        {icon}
      </IconButton>
    </span>
  </Tooltip>
);

const STATUS_TO_CHIP_COLOR: Record<
  RaffleRoundStatus,
  "default" | "info" | "warning" | "success" | "error"
> = {
  [RaffleRoundStatus.Draft]: "default",
  [RaffleRoundStatus.Announced]: "info",
  [RaffleRoundStatus.Closed]: "warning",
  [RaffleRoundStatus.Drawn]: "success",
  [RaffleRoundStatus.Cancelled]: "error",
};

export const RaffleRoundsTable = ({
  rounds,
  canManage,
  canExport,
  busyRoundIDs,
  closingRoundIDs,
  isExporting,
  emptyText,
  onView,
  onEdit,
  onAnnounce,
  onClose,
  onComplete,
  onCancel,
  onSetDefault,
  onExport,
  onFindHolder,
}: Props) => {
  const { t } = useResource();
  const { i18n } = useTranslation();

  const statusLabel: Record<RaffleRoundStatus, string> = {
    [RaffleRoundStatus.Draft]: t(RESOURCES.Status.Draft),
    [RaffleRoundStatus.Announced]: t(RESOURCES.Status.Announced),
    [RaffleRoundStatus.Closed]: t(RESOURCES.Status.Closed),
    [RaffleRoundStatus.Drawn]: t(RESOURCES.Status.Drawn),
    [RaffleRoundStatus.Cancelled]: t(RESOURCES.Status.Cancelled),
  };

  return (
    <Box display="grid" alignItems="start" sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t(RESOURCES.Table.Region)}</TableCell>
            <TableCell>{t(RESOURCES.Table.Name)}</TableCell>
            <TableCell>{t(RESOURCES.Table.Type)}</TableCell>
            <TableCell>{t(RESOURCES.Table.Status)}</TableCell>
            <TableCell>{t(RESOURCES.Table.DrawDate)}</TableCell>
            <TableCell align="right">{t(RESOURCES.Table.Tickets)}</TableCell>
            <TableCell align="right">
              {t(RESOURCES.Table.Participants)}
            </TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rounds.length === 0 && (
            <TableRow>
              <TableCell colSpan={8}>
                <Typography color="text.secondary" py={2} textAlign="center">
                  {emptyText}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {groupByDrawMonth(rounds).map((group) => (
            <Fragment key={group.key ?? "unscheduled"}>
              <TableRow>
                <TableCell colSpan={8} variant="head">
                  {group.key === null
                    ? t(RESOURCES.Table.Unscheduled)
                    : formatMonthAndYear(
                        Number(group.key.slice(0, 4)),
                        Number(group.key.slice(5, 7)),
                        i18n.language,
                      )}
                </TableCell>
              </TableRow>
              {group.rounds.map((round) => {
                const isDraft = round.status === RaffleRoundStatus.Draft;
                const isAnnounced =
                  round.status === RaffleRoundStatus.Announced;
                const isClosed = round.status === RaffleRoundStatus.Closed;
                const isDrawn = round.status === RaffleRoundStatus.Drawn;
                const isBusy = busyRoundIDs.includes(round.publicID);
                const isClosing = closingRoundIDs.includes(round.publicID);

                return (
                  <TableRow key={round.publicID}>
                    <TableCell>{round.region}</TableCell>
                    <TableCell>{round.name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant={round.isDefault ? "filled" : "outlined"}
                        color={round.isDefault ? "primary" : "default"}
                        label={
                          round.isDefault
                            ? t(RESOURCES.Table.TypeDefault)
                            : t(RESOURCES.Table.TypeCompetition)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusLabel[round.status]}
                        color={STATUS_TO_CHIP_COLOR[round.status]}
                      />
                    </TableCell>
                    <TableCell>
                      {round.drawDate ? formatDate(round.drawDate) : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {round.ticketCount ?? round.waitingTicketCount ?? 0}
                    </TableCell>
                    <TableCell align="right">
                      {round.participantCount ?? "—"}
                    </TableCell>
                    <TableCell align="right">
                      {/* Drafts too: AdminLite's only view of a draft's prizes. */}
                      <RowAction
                        title={t(RESOURCES.Actions.View)}
                        icon={<VisibilityIcon fontSize="small" />}
                        onClick={() => onView(round)}
                      />
                      {canManage && isDraft && (
                        <>
                          <RowAction
                            title={t(RESOURCES.Actions.Edit)}
                            icon={<EditIcon fontSize="small" />}
                            isDisabled={isBusy}
                            onClick={() => onEdit(round)}
                          />
                          <RowAction
                            title={
                              round.canAnnounce
                                ? t(RESOURCES.Actions.Announce)
                                : t(RESOURCES.Messages.AnnounceRequirements)
                            }
                            icon={<CampaignIcon fontSize="small" />}
                            isDisabled={isBusy || !round.canAnnounce}
                            onClick={() => onAnnounce(round)}
                          />
                          {!round.isDefault && (
                            <RowAction
                              title={t(RESOURCES.Actions.Cancel)}
                              icon={<DeleteOutlineIcon fontSize="small" />}
                              isDisabled={isBusy}
                              onClick={() => onCancel(round)}
                            />
                          )}
                        </>
                      )}
                      {canManage &&
                        (isDraft || isAnnounced) &&
                        !round.isDefault && (
                          <RowAction
                            title={t(RESOURCES.Actions.SetDefault)}
                            icon={<StarOutlineIcon fontSize="small" />}
                            isDisabled={isBusy}
                            onClick={() => onSetDefault(round)}
                          />
                        )}
                      {canManage && isAnnounced && (
                        <RowAction
                          title={t(RESOURCES.Actions.Close)}
                          icon={
                            isClosing ? (
                              <CircularProgress size={20} />
                            ) : (
                              <LockIcon fontSize="small" />
                            )
                          }
                          isDisabled={isBusy}
                          onClick={() => onClose(round)}
                        />
                      )}
                      {canExport && (isClosed || isDrawn) && (
                        <RowAction
                          title={t(RESOURCES.Actions.Export)}
                          icon={<DownloadIcon fontSize="small" />}
                          isDisabled={isExporting}
                          onClick={() => onExport(round)}
                        />
                      )}
                      {/* Needs only Raffle Read, which the route already requires. */}
                      {(isClosed || isDrawn) && (
                        <RowAction
                          title={t(RESOURCES.Actions.FindHolder)}
                          icon={<PersonSearchIcon fontSize="small" />}
                          onClick={() => onFindHolder(round)}
                        />
                      )}
                      {canManage && isClosed && (
                        <RowAction
                          title={t(RESOURCES.Actions.Complete)}
                          icon={<TaskAltIcon fontSize="small" />}
                          isDisabled={isBusy}
                          onClick={() => onComplete(round)}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
